import { activePortalRegion } from '../config/portal-regions';
const publicApiBase = () => (activePortalRegion.publicApiUrl || (activePortalRegion.id === 'us' ? import.meta.env.VITE_PUBLIC_API_URL : 'https://regional-endpoint-not-configured.invalid')) || '';

export class ForgotPasswordApiError extends Error {
  status: number;
  retryAfter?: number;
  restart?: boolean;

  constructor(message: string, status: number, retryAfter?: number, restart?: boolean) {
    super(message);
    this.name = 'ForgotPasswordApiError';
    this.status = status;
    this.retryAfter = retryAfter;
    this.restart = restart;
  }
}

function waitMessage(retryAfter?: number) {
  const wait = Math.max(0, Number(retryAfter) || 0);
  if (!wait) return '';
  if (wait < 60) return ` Try again in ${Math.max(1, Math.ceil(wait))} seconds.`;
  const minutes = Math.ceil(wait / 60);
  if (minutes < 60) return ` Try again in ${minutes} minutes.`;
  return ` Try again in ${Math.ceil(minutes / 60)} hours.`;
}

async function postForgotPassword(body: Record<string, unknown>) {
  const response = await fetch(`${publicApiBase()}/auth-forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  const retryAfter = Number(data.retryAfter) || undefined;
  if (!response.ok) {
    const base = data.error || 'Password reset request failed';
    const message = response.status === 429 && !base.includes('Try again')
      ? `${base}${waitMessage(retryAfter)}`
      : base;
    throw new ForgotPasswordApiError(
      message,
      response.status,
      retryAfter,
      Boolean(data.restart) || response.status === 429,
    );
  }
  return data;
}

export async function startForgotPassword(email: string): Promise<string> {
  if (!publicApiBase() || !email.trim()) {
    throw new Error('Password reset is unavailable right now.');
  }
  const data = await postForgotPassword({
    action: 'start',
    email: email.trim().toLowerCase(),
  });
  if (!data.flowId) {
    throw new ForgotPasswordApiError('Password reset is unavailable right now.', 503, undefined, true);
  }
  return String(data.flowId);
}

export async function confirmEmailForgotPassword(
  email: string,
  flowId: string,
  emailCode: string,
  newPassword: string,
): Promise<void> {
  await postForgotPassword({
    action: 'confirm-email',
    email: email.trim().toLowerCase(),
    flowId,
    emailCode,
    newPassword,
  });
}

export async function confirmOwnerForgotPassword(
  email: string,
  emailCode: string,
  newPassword: string,
): Promise<void> {
  await postForgotPassword({
    action: 'confirm-owner',
    email: email.trim().toLowerCase(),
    emailCode,
    newPassword,
  });
}
